/**
 * MAHI — Welcome (new users only, one screen, two fields).
 * Name + gender is the only thing asked before Home — it's what lets MAHI
 * personalize try-on and copy immediately. Everything else (email, photo,
 * style picks) lives in Profile and is optional.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mira, { errMsg, trackEvent } from '../api';
import { useMiraAuth } from '../auth';
import MiraRobot from '../components/MiraRobot';
import MiraShell from '../components/MiraShell';

const GENDERS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
];

export default function MiraWelcome() {
  const navigate = useNavigate();
  const { user, refresh } = useMiraAuth();
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = 'MAHI — Welcome'; }, []);

  // A returning user (or one who already finished this step) has nothing to
  // do here — send them straight to Home instead of re-showing the form.
  useEffect(() => {
    if (user?.name && user?.gender) navigate('/mira/home', { replace: true });
  }, [user, navigate]);

  const submit = async () => {
    if (!name.trim()) return toast.error('What should MAHI call you?');
    if (!gender) return toast.error('Pick one so try-ons look right');
    setBusy(true);
    try {
      await mira.patch('/profile/basic', { name: name.trim(), gender });
      trackEvent('profile_basic_saved');
      await refresh();
      navigate('/mira/home', { replace: true });
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
    finally { setBusy(false); }
  };

  return (
    <MiraShell>
      <div className="min-h-screen flex items-center justify-center px-5 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <div className="flex flex-col items-center mb-8">
            <MiraRobot size={88} say="Hi! I'm MAHI. What should I call you?" />
          </div>

          <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-[0_24px_60px_-30px_rgba(15,23,42,0.15)] p-7">
            <h2 className="text-2xl font-semibold text-slate-900 mb-1">Quick intro</h2>
            <p className="text-sm text-slate-500 mb-6">Two things, then you're in.</p>

            <label className="block text-xs font-medium text-slate-500 mb-2">Your name</label>
            <input
              data-testid="welcome-name-input"
              value={name} onChange={(e) => setName(e.target.value)} autoFocus
              className="w-full rounded-xl border border-slate-200 px-4 py-3 mb-5 focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-300"
              placeholder="e.g. Alice"
            />

            <label className="block text-xs font-medium text-slate-500 mb-2">Gender</label>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {GENDERS.map((g) => (
                <button
                  key={g.value} type="button" data-testid={`welcome-gender-${g.value}`}
                  onClick={() => setGender(g.value)}
                  className={`rounded-xl py-3 text-sm font-medium border transition ${
                    gender === g.value
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                  }`}
                >{g.label}</button>
              ))}
            </div>

            <button onClick={submit} disabled={busy} data-testid="welcome-continue-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-6 py-3.5 font-medium hover:bg-slate-800 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </motion.div>
      </div>
    </MiraShell>
  );
}
